// Client-side asset preparation. Images become WebP and audio becomes MP3 in the browser, so
// only the compressed file is ever uploaded. If the browser cannot produce the target format the
// original file is returned unchanged and the backend conversion (building novels) still applies.
// A GIF is refused: animations must be uploaded as an animated WebP of at most 7.5 MB.

const WEBP_QUALITY = 0.85;
const MAX_ANIMATED_BYTES = 7.5 * 1024 * 1024;
const MP3_SAMPLE_RATE = 44100; // decodeAudioData resamples to this, avoiding encoder-unsupported rates
const MP3_KBPS = 128;
const MP3_CHUNK_FRAMES = 1152 * 20;

const GIF_REJECTED = 'GIF не поддерживается. Загрузите анимированный WebP размером не более 7,5 МБ';
const ANIMATED_TOO_BIG = 'Анимированный WebP больше 7,5 МБ. Уменьшите разрешение или длительность анимации';

const getExtension = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

const withExtension = (name, extension) => `${name.replace(/\.[^.]+$/, '')}.${extension}`;

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// A WebP is animated when the animation flag (0x02) in the flags byte of its VP8X chunk is set.
const isAnimatedWebp = async (file) => {
  const head = new Uint8Array(await file.slice(0, 21).arrayBuffer());
  return String.fromCharCode(...head.slice(12, 16)) === 'VP8X' && (head[20] & 0x02) !== 0;
};

const convertStaticToWebp = async (file) => {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('Не удалось прочитать изображение. Проверьте, что файл не повреждён');
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    const blob = await canvasToBlob(canvas, 'image/webp', WEBP_QUALITY);
    // Browsers without WebP encoding silently hand back a PNG
    if (!blob || blob.type !== 'image/webp') return file;
    return new File([blob], withExtension(file.name, 'webp'), { type: 'image/webp' });
  } finally {
    bitmap.close();
  }
};

const convertImageToWebp = async (file) => {
  const extension = getExtension(file.name);

  if (extension === 'gif' || file.type === 'image/gif') throw new Error(GIF_REJECTED);
  if (extension === 'webp') {
    // An animated WebP is uploaded as is, so its size is checked here
    if (file.size > MAX_ANIMATED_BYTES && (await isAnimatedWebp(file))) throw new Error(ANIMATED_TOO_BIG);
    return file;
  }
  return convertStaticToWebp(file);
};

const toInt16 = (samples) => {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

const convertAudioToMp3 = async (file) => {
  if (getExtension(file.name) === 'mp3') return file;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return file;

  let audioBuffer;
  const context = new AudioContextClass({ sampleRate: MP3_SAMPLE_RATE });
  try {
    audioBuffer = await context.decodeAudioData(await file.arrayBuffer());
  } catch {
    return file;
  } finally {
    context.close();
  }

  const { Mp3Encoder } = await import('@breezystack/lamejs');
  const channels = Math.min(audioBuffer.numberOfChannels, 2);
  const encoder = new Mp3Encoder(channels, audioBuffer.sampleRate, MP3_KBPS);
  const left = toInt16(audioBuffer.getChannelData(0));
  const right = channels === 2 ? toInt16(audioBuffer.getChannelData(1)) : undefined;

  const parts = [];
  for (let i = 0; i < left.length; i += MP3_CHUNK_FRAMES) {
    const chunk = encoder.encodeBuffer(
      left.subarray(i, i + MP3_CHUNK_FRAMES),
      right ? right.subarray(i, i + MP3_CHUNK_FRAMES) : undefined
    );
    if (chunk.length > 0) parts.push(chunk);
    // Yield so the page stays responsive while a long track encodes
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  parts.push(encoder.flush());

  return new File(parts, withExtension(file.name, 'mp3'), { type: 'audio/mpeg' });
};

// Returns the file that should actually be uploaded for the given asset type.
// Throws an Error with a user-facing (Russian) message when the file must be rejected.
export const prepareAssetFile = async (file, assetType) => {
  if (assetType === 'music') {
    if (!file.type.startsWith('audio/')) throw new Error('Для музыки выберите аудиофайл');
    return convertAudioToMp3(file);
  }
  if (!file.type.startsWith('image/')) throw new Error('Для спрайтов и фонов выберите изображение');
  return convertImageToWebp(file);
};
